import type {
    GetProfilePayload,
    GetProfileResponse,
} from '~~/layers/base/server/api/profile/index.get'

/** Profile row fields needed for staff/admin route middleware. */
export type ProfileForRouteGuard = GetProfilePayload

/**
 * Loads profile role flags for route guards.
 *
 * - **Server:** `$fetch` to `GET /api/profile` with the incoming request cookies (SSR/nitro).
 * - **Client:** `useProfileStore().ensureProfileLoaded()` so staff/admin checks reuse the same
 *   in-memory cache as the top nav and avoid redundant network calls when the plugin already
 *   populated the store.
 */
export async function fetchProfileForRouteGuard(): Promise<ProfileForRouteGuard | null> {
    if (import.meta.server) {
        const event = useRequestEvent()
        if (!event) {
            return null
        }
        const { getRequestHeader, getRequestURL } = await import('h3')
        const url = getRequestURL(event)
        const cookie = getRequestHeader(event, 'cookie')
        try {
            const res = await $fetch<GetProfileResponse>(`${url.origin}/api/profile`, {
                headers: cookie ? { cookie } : {},
            })
            return res.profile ?? null
        } catch {
            return null
        }
    }

    const profileStore = useProfileStore()
    return (await profileStore.ensureProfileLoaded()) ?? null
}
